import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
    getAuth,
    Auth,
    connectAuthEmulator,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithEmailAndPassword,
    signInWithEmailLink,
    isSignInWithEmailLink,
    sendPasswordResetEmail,
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
    GoogleAuthProvider,
    OAuthProvider,
    SAMLAuthProvider,
    signOut,
    onAuthStateChanged,
    User as FirebaseUser,
    getIdToken
} from 'firebase/auth';
import { environment } from '../../../environments/environment';
import { FirebaseAnalyticsService } from './firebase-analytics.service';

export interface FirebaseConfig {
    projectId: string;
    apiKey?: string | null;
    authDomain?: string | null;
    appId?: string | null;
    measurementId?: string | null;
    useEmulator: boolean;
    emulatorHost?: string | null;
    authPort?: number | null;
    enabledProviders?: string[];
}

@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
    private app: FirebaseApp | null = null;
    private auth: Auth | null = null;
    private configLoaded = signal(false);
    private config: FirebaseConfig | null = null;

    // Firebase user state
    firebaseUser = signal<FirebaseUser | null>(null);
    firebaseLoading = signal(true);
    firebaseError = signal<string | null>(null);

    // Enabled providers
    enabledProviders = signal<string[]>(['email']);

    isFirebaseAuthenticated = computed(() => !!this.firebaseUser());

    // Provider availability checks
    isEmailEnabled = computed(() => this.enabledProviders().includes('email'));
    isGoogleEnabled = computed(() => this.enabledProviders().includes('google'));
    isMicrosoftEnabled = computed(() => this.enabledProviders().includes('microsoft'));
    isSamlEnabled = computed(() => this.enabledProviders().some(p => p.startsWith('saml')));

    private http = inject(HttpClient);
    private analytics = inject(FirebaseAnalyticsService);

    constructor() {
        this.initializeFirebase();
    }

    private async initializeFirebase(): Promise<void> {
        try {
            // Fetch Firebase configuration from the backend
            const config = await this.http.get<FirebaseConfig>(`${environment.apiBaseUrl}/auth/firebase/config`).toPromise();

            if (!config || !config.projectId) {
                throw new Error('Failed to load Firebase configuration');
            }

            this.config = config;

            // Store enabled providers
            if (config.enabledProviders && config.enabledProviders.length > 0) {
                this.enabledProviders.set(config.enabledProviders);
            }

            // Build Firebase config object
            const firebaseConfig: any = {
                projectId: config.projectId,
                apiKey: config.apiKey || 'demo-api-key' // Required but not validated by emulator
            };

            // Add authDomain if provided (required for OAuth popup providers in production)
            if (config.authDomain) {
                firebaseConfig.authDomain = config.authDomain;
            }

            // Add appId and measurementId for Firebase Analytics
            if (config.appId) {
                firebaseConfig.appId = config.appId;
            }
            if (config.measurementId) {
                firebaseConfig.measurementId = config.measurementId;
            }

            // Initialize Firebase app
            this.app = initializeApp(firebaseConfig);

            this.auth = getAuth(this.app);

            // Initialize Firebase Analytics (disabled in emulator mode)
            await this.analytics.initialize(this.app, config.useEmulator);

            // Connect to emulator if configured
            if (config.useEmulator && config.authPort) {
                // Browser needs localhost, not the Docker internal hostname
                const emulatorHost = (typeof window !== 'undefined') ? 'localhost' : (config.emulatorHost || 'localhost');
                const emulatorUrl = `http://${emulatorHost}:${config.authPort}`;
                console.log('Connecting to Firebase Auth emulator:', emulatorUrl);
                connectAuthEmulator(this.auth, emulatorUrl, { disableWarnings: true });
            }

            // Listen for auth state changes
            onAuthStateChanged(this.auth, (user) => {
                console.log('Firebase auth state changed:', user?.uid);
                this.firebaseUser.set(user);
                this.firebaseLoading.set(false);

                // Update analytics user ID
                this.analytics.setUserId(user?.uid || null);
            });

            this.configLoaded.set(true);
            console.log('Firebase Auth initialized successfully, enabled providers:', config.enabledProviders);
        } catch (error) {
            console.error('Failed to initialize Firebase:', error);
            this.firebaseError.set('Failed to initialize Firebase authentication');
            this.firebaseLoading.set(false);
        }
    }

    async waitForInit(): Promise<void> {
        // Return immediately if already initialized
        if (this.configLoaded()) {
            return;
        }

        // Wait for initialization with a Promise-based approach
        return new Promise<void>((resolve, reject) => {
            const maxWait = 10000; // 10 seconds timeout
            const startTime = Date.now();

            const checkInit = () => {
                if (this.configLoaded()) {
                    resolve();
                } else if (Date.now() - startTime > maxWait) {
                    reject(new Error('Firebase initialization timeout'));
                } else {
                    setTimeout(checkInit, 100);
                }
            };

            checkInit();
        });
    }

    async signInWithEmailAndPassword(email: string, password: string): Promise<FirebaseUser | null> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            const result = await signInWithEmailAndPassword(this.auth, email, password);
            this.analytics.logLogin('email');
            return result.user;
        } catch (error: any) {
            console.error('Email sign-in failed:', error);
            this.analytics.logError('auth', error.code || 'email_signin_failed', 'signInWithEmailAndPassword');
            // Provide user-friendly error messages
            let errorMessage = 'Sign-in failed';
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                errorMessage = 'Invalid email or password';
            } else if (error.code === 'auth/user-disabled') {
                errorMessage = 'This account has been disabled';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many failed attempts. Please try again later';
            }
            this.firebaseError.set(errorMessage);
            throw error;
        } finally {
            this.firebaseLoading.set(false);
        }
    }

    async sendPasswordResetEmail(email: string): Promise<void> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            await sendPasswordResetEmail(this.auth, email);
        } catch (error: any) {
            console.error('Password reset failed:', error);
            // Don't expose whether user exists for security
            // Just log the error but don't show to user
        } finally {
            this.firebaseLoading.set(false);
        }
    }

    async signInWithGoogle(): Promise<FirebaseUser | null> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');

            const result = await signInWithPopup(this.auth, provider);
            this.analytics.logLogin('google');
            return result.user;
        } catch (error: any) {
            console.error('Google sign-in failed:', error);
            this.analytics.logError('auth', error.code || 'google_signin_failed', 'signInWithGoogle');
            this.firebaseError.set(error.message || 'Google sign-in failed');
            throw error;
        } finally {
            this.firebaseLoading.set(false);
        }
    }

    async signInWithMicrosoft(): Promise<FirebaseUser | null> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            const provider = new OAuthProvider('microsoft.com');
            provider.addScope('User.Read');

            const result = await signInWithPopup(this.auth, provider);
            this.analytics.logLogin('microsoft');
            return result.user;
        } catch (error: any) {
            console.error('Microsoft sign-in failed:', error);
            this.analytics.logError('auth', error.code || 'microsoft_signin_failed', 'signInWithMicrosoft');
            this.firebaseError.set(error.message || 'Microsoft sign-in failed');
            throw error;
        } finally {
            this.firebaseLoading.set(false);
        }
    }

    async signInWithSAML(providerId: string = 'saml.enterprise'): Promise<FirebaseUser | null> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            const provider = new SAMLAuthProvider(providerId);

            const result = await signInWithPopup(this.auth, provider);
            this.analytics.logLogin('saml');
            return result.user;
        } catch (error: any) {
            console.error('SAML sign-in failed:', error);
            this.analytics.logError('auth', error.code || 'saml_signin_failed', 'signInWithSAML');
            this.firebaseError.set(error.message || 'SAML sign-in failed');
            throw error;
        } finally {
            this.firebaseLoading.set(false);
        }
    }

    // --- Redirect-based provider sign-in -------------------------------------------------
    //
    // For contexts where a popup can never open — notably the mobile auth bridge running
    // inside ASWebAuthenticationSession, where signInWithPopup always fails with
    // auth/popup-blocked. These navigate away from the page; the caller resolves the result
    // with completeRedirectSignIn() when the page loads again. The web portal keeps using
    // the popup variants above, which are a better experience in a real browser.

    async signInWithGoogleRedirect(): Promise<void> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        const provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        await signInWithRedirect(this.auth, provider);
    }

    async signInWithMicrosoftRedirect(): Promise<void> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        const provider = new OAuthProvider('microsoft.com');
        provider.addScope('User.Read');
        await signInWithRedirect(this.auth, provider);
    }

    async signInWithSAMLRedirect(providerId: string = 'saml.enterprise'): Promise<void> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        await signInWithRedirect(this.auth, new SAMLAuthProvider(providerId));
    }

    /**
     * Resolves a pending redirect sign-in. Returns null on a normal page load (no redirect
     * in flight), so it is safe to call unconditionally on init.
     */
    async completeRedirectSignIn(): Promise<FirebaseUser | null> {
        await this.waitForInit();
        if (!this.auth) return null;

        try {
            const result = await getRedirectResult(this.auth);
            if (!result?.user) return null;
            this.analytics.logLogin(this.loginMethodFor(result.providerId));
            return result.user;
        } catch (error: any) {
            console.error('Redirect sign-in failed:', error);
            this.analytics.logError('auth', error.code || 'redirect_signin_failed', 'completeRedirectSignIn');
            this.firebaseError.set(error.message || 'Sign-in failed');
            throw error;
        }
    }

    /** Maps a Firebase provider id onto the analytics login-method union. */
    private loginMethodFor(providerId: string | null): 'google' | 'microsoft' | 'email' | 'saml' {
        if (!providerId) return 'email';
        if (providerId.startsWith('saml.')) return 'saml';
        if (providerId.includes('google')) return 'google';
        if (providerId.includes('microsoft')) return 'microsoft';
        return 'email';
    }

    async signOutFirebase(): Promise<void> {
        await this.waitForInit();
        if (!this.auth) return;

        try {
            await signOut(this.auth);
        } catch (error) {
            console.error('Firebase sign-out failed:', error);
        }
    }

    async getIdToken(): Promise<string | null> {
        await this.waitForInit();
        const user = this.firebaseUser();
        if (!user) return null;

        try {
            return await getIdToken(user, true);
        } catch (error) {
            console.error('Failed to get ID token:', error);
            return null;
        }
    }

    /**
     * Check if the current URL is a Firebase email sign-in link.
     * Used to detect invite or magic link sign-in flows.
     */
    isEmailSignInLink(url?: string): boolean {
        if (!this.auth) return false;
        return isSignInWithEmailLink(this.auth, url || window.location.href);
    }

    /**
     * Complete sign-in with email link (for invites).
     * The email must match the one that was sent the invite.
     */
    async signInWithEmailLink(email: string, url?: string): Promise<FirebaseUser | null> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            const link = url || window.location.href;
            const result = await signInWithEmailLink(this.auth, email, link);
            this.analytics.logLogin('email_link');
            return result.user;
        } catch (error: any) {
            console.error('Email link sign-in failed:', error);
            this.analytics.logError('auth', error.code || 'email_link_signin_failed', 'signInWithEmailLink');
            let errorMessage = 'Sign-in failed';
            if (error.code === 'auth/invalid-action-code') {
                errorMessage = 'This link has expired or already been used';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'The email address does not match the invitation';
            }
            this.firebaseError.set(errorMessage);
            throw error;
        } finally {
            this.firebaseLoading.set(false);
        }
    }

    /**
     * Change password for email/password authenticated users.
     * Requires re-authentication with current password for security.
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        await this.waitForInit();
        if (!this.auth) throw new Error('Firebase Auth not initialized');

        const user = this.firebaseUser();
        if (!user) throw new Error('No user signed in');
        if (!user.email) throw new Error('User has no email address');

        try {
            this.firebaseLoading.set(true);
            this.firebaseError.set(null);

            // Re-authenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);
        } catch (error: any) {
            console.error('Password change failed:', error);
            let errorMessage = 'Failed to change password';
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                errorMessage = 'Current password is incorrect';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'New password is too weak. Use at least 6 characters';
            } else if (error.code === 'auth/requires-recent-login') {
                errorMessage = 'Please sign out and sign in again before changing your password';
            }
            this.firebaseError.set(errorMessage);
            throw new Error(errorMessage);
        } finally {
            this.firebaseLoading.set(false);
        }
    }
}
